import { useState } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BusinessFunction, ViewMode } from "../../types";
import { useAppStore } from "../../store";
import { ToolItem } from "../tools/ToolItem";
import { DragHandle } from "../shared/DragHandle";

interface FunctionCardProps {
  stackId: string;
  func: BusinessFunction;
  viewMode: ViewMode;
}

export function FunctionCard({ stackId, func, viewMode }: FunctionCardProps) {
  const { updateFunction, deleteFunction, addNewTool, moveTool } =
    useAppStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `func:${stackId}:${func.id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [nameValue, setNameValue] = useState(func.name);

  const handleEdit = () => {
    setNameValue(func.name);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (nameValue.trim()) {
      updateFunction(stackId, func.id, { name: nameValue.trim() });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setNameValue(func.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleDelete = () => {
    if (confirm(`Delete function "${func.name}"?`)) {
      deleteFunction(stackId, func.id);
    }
  };

  const handleAddTool = () => {
    addNewTool(stackId, func.id);
  };

  const handleToolDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Parse IDs: tool:stackId:funcId:toolId
    const activeParts = activeId.split(":");
    const overParts = overId.split(":");

    if (activeParts[0] !== "tool" || overParts[0] !== "tool") return;

    const fromStackId = activeParts[1];
    const fromFuncId = activeParts[2];
    const toolId = activeParts[3];
    const toStackId = overParts[1];
    const toFuncId = overParts[2];

    // Find the target index
    const targetFunc = func;
    const toIndex = targetFunc.tools.findIndex(
      (t) => `tool:${toStackId}:${toFuncId}:${t.id}` === overId,
    );

    moveTool(fromStackId, fromFuncId, toStackId, toFuncId, toolId, toIndex);
  };

  // Filter tools based on view mode
  const filteredTools = func.tools.filter((tool) => {
    if (viewMode === "All") return true;
    return tool.status === viewMode;
  });

  const toolIds = filteredTools.map(
    (tool) => `tool:${stackId}:${func.id}:${tool.id}`,
  );

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ bgcolor: "background.paper" }}
    >
      {/* Function Header */}
      <Box
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          borderBottom: isExpanded ? 1 : 0,
          borderColor: "divider",
        }}
      >
        <DragHandle
          listeners={listeners}
          attributes={attributes}
          size="small"
        />
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ p: 0.5 }}
        >
          {isExpanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>

        {isEditing ? (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}
          >
            <TextField
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              autoFocus
              fullWidth
              sx={{ "& .MuiInputBase-input": { py: 0.5 } }}
            />
            <IconButton size="small" onClick={handleSave}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                flex: 1,
                cursor: "pointer",
                "&:hover": { color: "primary.main" },
              }}
              onClick={handleEdit}
            >
              {func.name}
            </Typography>
            <IconButton size="small" onClick={handleEdit} sx={{ p: 0.5 }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleDelete} sx={{ p: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      {/* Tools List */}
      <Collapse in={isExpanded}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleToolDragEnd}
        >
          <SortableContext
            items={toolIds}
            strategy={verticalListSortingStrategy}
          >
            <List dense sx={{ py: 0 }}>
              {filteredTools.map((tool) => (
                <ToolItem
                  key={tool.id}
                  stackId={stackId}
                  funcId={func.id}
                  tool={tool}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
        <Box sx={{ p: 1, pt: 0 }}>
          <IconButton size="small" onClick={handleAddTool} title="Add Tool">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Collapse>
    </Paper>
  );
}
